import {
  ChangeDetectionStrategy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {OnDestroy$} from "@pdtec/ngx-observable-lifecycle";
import {
  HttpSearchResultTag,
} from "../../../models";
import {ToursStateService} from "../../../services/tours-state.service";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {AnalyticService} from "../../../services/analytic.service";

@Component({
  selector: 'app-tags-filter',
  templateUrl: './tags-filter.component.html',
  styleUrls: ['./tags-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class TagsFilterComponent extends OnDestroy$ implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tagList') tagList: ElementRef<HTMLElement>;
  @ViewChildren('tagButton') tagButtons: QueryList<ElementRef<HTMLButtonElement>>;
  visibleTagsCount = 0;
  private resizeObserver: ResizeObserver;
  private measureFrame: number;
  private destroyed = false;
  @Input() tags: HttpSearchResultTag[];
  @Output() filterChanged = new EventEmitter<void>();

  allowTagCount: number = 3;
  selectedTagCount: number = 0;
  selectedTagProgress: number = 0;

  isShowMoreTags: boolean = false;
  applyedTagsIds = [];

  private destroy$ = new Subject<void>();

  constructor(
    private state: ToursStateService,
    private cdRef: ChangeDetectorRef,
    private analyticService: AnalyticService
  ) {
    super();
  }

  ngOnInit(): void {
    const smartfilters = this.state.getState().selectedFilters.smartfilters;
    this.applyedTagsIds = smartfilters?.tagsIds ?? [];
    this.selectedTagCount = this.applyedTagsIds.length;
    this.updateSelectedTagProgress();
    if (this.tags?.length) {
      this.sortTags();
    }

    this.state.updated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.applyedTagsIds = state.selectedFilters.smartfilters?.tagsIds ?? [];
        this.selectedTagCount = this.applyedTagsIds.length;
        this.updateSelectedTagProgress();
        if (this.tags?.length) {
          this.sortTags();
        }
        this.cdRef.detectChanges();
      });
  }



  ngAfterViewInit(): void {
    let lastWidth = -1;
    this.resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width !== lastWidth) {
        lastWidth = entry.contentRect.width;
        this.scheduleMeasure();
      }
    });
    this.resizeObserver.observe(this.tagList.nativeElement);
    this.tagButtons.changes.pipe(takeUntil(this.destroy$)).subscribe(() => this.scheduleMeasure());
    document.fonts.ready.then(() => this.scheduleMeasure());
    this.scheduleMeasure();
  }

  private scheduleMeasure(): void {
    if (this.destroyed || !this.tagButtons) return;
    cancelAnimationFrame(this.measureFrame);
    this.measureFrame = requestAnimationFrame(() => {
      const buttons = this.tagButtons.map(ref => ref.nativeElement);
      // Measure the full wrapped list before restoring the collapsed state.
      buttons.forEach(button => button.classList.remove('hide'));
      let row = 0;
      let previousTop = -Infinity;
      let count = 0;
      for (const button of buttons) {
        const top = button.getBoundingClientRect().top;
        if (Math.abs(top - previousTop) > 1) {
          row++;
          previousTop = top;
        }
        if (row > 3) break;
        count++;
      }
      this.visibleTagsCount = count;
      buttons.forEach((button, index) => button.classList.toggle('hide', !this.isShowMoreTags && index >= count));
      this.cdRef.detectChanges();
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.measureFrame);
    this.destroy$.next();
    this.destroy$.complete();
  }

  showMoreTags() {
    this.isShowMoreTags = !this.isShowMoreTags;
  }

  get showMoreTagsLabel() {
    const tagsCount = this.tags.length;
    if (tagsCount > this.visibleTagsCount) {
      const moreCount = tagsCount - this.visibleTagsCount;
      return this.isShowMoreTags ? 'Свернуть' : 'Показать еще ' + moreCount;
    }

    return '';
  }

  selectTag(tagId: number): void {
    const isActive = this.isActiveTag(tagId);
    if (!isActive && this.selectedTagCount >= this.allowTagCount) {
      return;
    }

    this.state.setSmartFilters(tagId);
    this.filterChanged.emit();
    this.applyedTagsIds = this.state.getState().selectedFilters.smartfilters?.tagsIds ?? [];
    this.selectedTagCount = this.applyedTagsIds.length;
    this.updateSelectedTagProgress();

    this.cdRef.detectChanges();

    if (!isActive) {
      this.analyticService.trackAnalyticEvent('click_tag_in_smart_filters', {
        tagId: tagId
      });
    }
  }

  hasSelectedFilter(): boolean {
    return this.state.hasSelectedFilter('smartfilters');
  }

  resetFilter(): void {
    this.state.resetFilter('smartfilters');
    this.applyedTagsIds = [];
    this.selectedTagCount = 0;
    this.updateSelectedTagProgress();
    this.filterChanged.emit();
    this.cdRef.detectChanges();
  }

  isTagDisabled(tagId: number): boolean {
    return !this.isActiveTag(tagId) && this.selectedTagCount >= this.allowTagCount;
  }

  isActiveTag(tagId) {
    return this.applyedTagsIds.indexOf(tagId) !== -1;
  }

  updateSelectedTagProgress() {
    this.selectedTagProgress = this.selectedTagCount * 33.33333;
  }

  trackByFn(_, item: HttpSearchResultTag) {
    return item.id;
  }

  private sortTags(): void {
    this.scheduleMeasure();
    this.tags.sort((a, b) => {
      const aIndex = this.applyedTagsIds.indexOf(a.id);
      const bIndex = this.applyedTagsIds.indexOf(b.id);

      // Если `a.id` или `b.id` есть в `applyedTagsIds`, они получают более высокий приоритет.
      if (aIndex > -1 && bIndex === -1) return -1;
      if (bIndex > -1 && aIndex === -1) return 1;

      // Если оба присутствуют в `applyedTagsIds`, сортируем по их порядку в массиве `applyedTagsIds`.
      if (aIndex > -1 && bIndex > -1) return aIndex - bIndex;

      // Если оба отсутствуют, оставляем исходный порядок.
      return 0;
    });
  }
}
